import * as React from 'react';
import { useTranslation } from 'react-i18next';
import styled from '@emotion/styled';

import { tokens, Icon, Text } from '@hotjar/hotsauce';

import type {
  BrowserName,
  DeviceType,
  HeatmapType,
  OperatingSystemFamily,
} from '@hotjar/webapp/domains/highlights/model';
import { capitalize } from '@hotjar/insights-common/lib/string';
import { timeAgo } from '@hotjar/webapp/lib/time';
import { SkeletonLoader } from './SkeletonLoader';

const HeadingSkeleton = styled(SkeletonLoader)`
  width: 110px;
  height: 20px;
  margin-bottom: ${tokens.spacer.x1};
`;

const DetailItemSkeleton = styled(SkeletonLoader)`
  width: 95px;
  height: 16px;
`;

const Wrapper = styled.section`
  padding: ${tokens.spacer.x2};
  padding-top: 0;
`;

const Heading = styled(Text)`
  margin: 0;
  margin-bottom: ${tokens.spacer.x1};
`;

const DetailsList = styled.dl`
  display: grid;
  grid-gap: ${tokens.spacer.x1};
  grid-template-columns: 1fr 1fr;
  margin: 0;
  padding: 0;
`;

const DetailItem = styled.div`
  align-items: center;
  display: flex;
  overflow: hidden;
`;

const Name = styled.dt`
  align-items: center;
  display: flex;
`;

const Value = styled.dd`
  ${tokens.text('caption')};
  ${tokens.truncateText()};

  color: ${tokens.color.text_default};
  margin-left: ${tokens.spacer.x1};
`;

interface DetailsProps {
  className?: string;
  highlight?:
    | {
        type: 'heatmap';
        metadata: {
          deviceType: DeviceType;
          heatmapType: HeatmapType;
          pagePath: string;
        };
      }
    | {
        type: 'recording';
        metadata: {
          browserName: BrowserName;
          countryCode: string;
          countryName: string;
          deviceType: DeviceType;
          osFamily: OperatingSystemFamily;
          osName: string;
          pagePath: string;
          recordingCreationEpochMs: number;
          screenResolution: [number, number];
          userId: string;
        };
      }
    | {
        type: 'unknown';
        metadata: Record<string, never>;
      };
}

type IconName = React.ComponentProps<typeof Icon>['icon'];

// eslint-disable-next-line consistent-return
function getHeatmapTypeIcon(heatmapType: HeatmapType): IconName {
  // eslint-disable-next-line default-case
  switch (heatmapType) {
    case 'click':
      return 'cursor_click';
    case 'movement':
      return 'cursor_move';
    case 'scroll':
      return 'scroll';
    case 'unknown':
      return 'question_cards';
  }
}

// eslint-disable-next-line consistent-return
function getDeviceTypeIcon(deviceType: DeviceType): IconName {
  // eslint-disable-next-line default-case
  switch (deviceType) {
    case 'desktop':
      return 'device_desktop';
    case 'phone':
      return 'device_phone';
    case 'tablet':
      return 'device_tablet';
    case 'unknown':
      return 'question_cards';
  }
}

function getOsFamilyIcon(osFamily: OperatingSystemFamily): IconName {
  if (osFamily === 'macos' || osFamily === 'ios') {
    return 'apple';
  }

  return osFamily;
}

function getBrowserIcon(browserName: BrowserName): IconName {
  if (browserName === 'ie') {
    return 'ms_ie';
  }

  return browserName;
}

function getCountryCode(countryCode: string): IconName {
  return countryCode.toLocaleLowerCase() as IconName;
}

export function Details({ className, highlight }: DetailsProps): JSX.Element | null {
  const { t } = useTranslation();

  function getDetails(): [
    Array<{
      icon: IconName;
      key: string;
      name: string;
      value: string;
    }>,
    string,
  ] {
    switch (highlight?.type) {
      case 'heatmap': {
        const { metadata } = highlight;
        return [
          [
            {
              icon: getDeviceTypeIcon(metadata.deviceType),
              key: 'device=type',
              name: t('highlights:heatmaps.details.device.name'),
              value: capitalize(metadata.deviceType),
            },
            {
              icon: getHeatmapTypeIcon(metadata.heatmapType),
              key: 'heatmap-type',
              name: t('highlights:heatmaps.details.type.name'),
              value: t(`highlights:heatmaps.details.type.${metadata.heatmapType}`),
            },
            {
              icon: 'page_empty' as const,
              key: 'page-path',
              name: t('highlights:heatmaps.details.page.name'),
              value: capitalize(metadata.pagePath),
            },
          ],
          t(`highlights:heatmaps.details.heading`),
        ];
      }
      case 'recording': {
        const { metadata } = highlight;
        return [
          [
            {
              icon: getDeviceTypeIcon(metadata.deviceType),
              key: 'resolution',
              name: t('highlights:recordings.details.resolution'),
              value: `${metadata.screenResolution[0]} ✕ ${metadata.screenResolution[1]}`,
            },
            {
              icon: 'user_single',
              key: 'user-id',
              name: t('highlights:recordings.details.user-id'),
              value: metadata.userId,
            },
            {
              icon: getOsFamilyIcon(metadata.osFamily),
              key: 'operating-system',
              name: t('highlights:recordings.details.operating-system'),
              value: capitalize(metadata.osName),
            },
            {
              icon: getCountryCode(metadata.countryCode),
              key: 'country',
              name: t('highlights:recordings.details.country'),
              value: capitalize(metadata.countryName),
            },
            {
              icon: getBrowserIcon(metadata.browserName),
              key: 'browser',
              name: t('highlights:recordings.details.browser'),
              value: capitalize(metadata.browserName),
            },
            {
              icon: 'time',
              key: 'recording-created',
              name: t('highlights:recordings.details.recording-created'),
              value: timeAgo(metadata.recordingCreationEpochMs),
            },
            {
              icon: 'page_empty',
              key: 'page',
              name: t('highlights:recordings.details.page'),
              value: capitalize(metadata.pagePath),
            },
          ],
          t(`highlights:recordings.details.heading`),
        ];
      }
      default:
        return [[], ''];
    }
  }

  if (!highlight) {
    return (
      <Wrapper className={className}>
        <HeadingSkeleton />
        <DetailsList>
          <DetailItemSkeleton />
          <DetailItemSkeleton />
          <DetailItemSkeleton />
          <DetailItemSkeleton />
          <DetailItemSkeleton />
        </DetailsList>
      </Wrapper>
    );
  }

  const [details, headingText] = getDetails();

  if (details.length === 0) {
    return null;
  }

  return (
    <Wrapper className={className}>
      <Heading as="p" textType="subtitle2">
        {headingText}
      </Heading>
      <DetailsList>
        {details.map(({ icon, key, name, value }) => (
          <DetailItem key={key}>
            <Name>
              {key === 'country' ? (
                <span className={`flag flag-${icon}`} />
              ) : (
                <Icon description={name} icon={icon} size="m" />
              )}
            </Name>
            <Value>{value}</Value>
          </DetailItem>
        ))}
      </DetailsList>
    </Wrapper>
  );
}
